import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraToolTip,
  FAIconName,
  FileIconsV2,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Icon,
  RowObject,
  BadFileAttachmentView,
  Text,
} from '@equip.health/ui';

import { DateTime } from 'luxon';

import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DOC_FILE_SOURCE } from '~/lib/constants/documentCenter.constants';
import { MESSAGE_INVALID_FILE } from '~/lib/constants/s3filestatus.contants';
import { AnalyticsContextType } from '~/lib/contexts/AnalyticsContext';
import type { History } from 'history';

import { getFileIconV2 } from '~/lib/util/chat/chat.utils';
import fileUtil from '~/lib/util/file.util';
import { navigateToViewNote } from '~/lib/util/patientDocuments.util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';

interface DocumentsTableRowProps {
  document: PatientDocumentV2;
  id: string;
  onVoidClick: (doc: PatientDocumentV2) => void;
  openEditDocumentModal: (document: PatientDocumentV2) => void;
  trackEvent: AnalyticsContextType['trackEvent'];
  history: History<unknown>;
  isInvalidFile?: boolean;
  onDownloadClick: () => void;
}
const INFECTED_FILE_STATUSES = 'INFECTED';

const DocumentsTableRow = ({
  document,
  id,
  onVoidClick,
  openEditDocumentModal,
  onDownloadClick,
  isInvalidFile = false,
  trackEvent,
  history,
}: DocumentsTableRowProps): RowObject<PatientDocumentsTableRow> => {
  const { createdBy, fileScanStatus } = document;

  const handleFileSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateToViewNote(history, document);
  };

  const uploadedBy = getDisplayNameOrLegalNameForProvider(
    createdBy?.displayFirstName,
    createdBy?.firstName,
    createdBy?.displayLastName,
    createdBy?.lastName,
  );
  const avatarSrc =
    createdBy?.providerAvatar?.thumbnailUrl || createdBy?.providerAvatar?.url;

  const shouldShowImage =
    !isInvalidFile &&
    fileUtil.isImageFile(document?.fileName) &&
    (document?.thumbnailUrl || document?.downloadUrl);
  const isInvalidDocument =
    fileScanStatus === INFECTED_FILE_STATUSES || isInvalidFile;

  const kebabOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Edit document properties',
      leftIcon: FAIconName.penRegular,
      leftIconColor: 'neutral.primary',
      onClick: () => {
        trackEvent(DocumentCenterEvents.fileKebabMenuEditClicked, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          DocumentExternalId: document.documentExternalId,
        });
        openEditDocumentModal(document);
      },
    },
    {
      label: 'Download file',
      leftIcon: FAIconName.arrowDownToLine,
      leftIconColor: 'neutral.primary',
      disabled: isInvalidDocument,
      onClick: () => {
        trackEvent(DocumentCenterEvents.fileKebabMenuDownloadClicked, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          DocumentExternalId: document.documentExternalId,
          FileName: document.fileName,
        });
        onDownloadClick();
      },
    },
    {
      label: 'Void document',
      labelColor: 'critical.base',
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'critical.base',
      onClick: () => {
        trackEvent(DocumentCenterEvents.fileKebabMenuVoidClicked, {
          Source: AnalyticsSourceValues.DOCUMENT_CENTER,
          DocumentExternalId: document.documentExternalId,
          FileName: document.fileName,
        });
        onVoidClick(document);
      },
    },
  ];

  const getSource = () => {
    return document?.noteIds?.length > 0 ? DOC_FILE_SOURCE.NOTE : '';
  };

  const source = getSource();

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleFileSourceClick(e as unknown as React.MouseEvent);
    }
  };

  return {
    key: document?.documentExternalId,
    object: {
      documentType: (
        <ChakraFlex
          flexDirection="column"
          id={`${id}__document-type`}
          marginLeft="12px"
          py={document?.documentTags?.length > 2 ? '12px' : '8px'}
        >
          {document?.documentTags?.length > 0 &&
            document.documentTags.map((tag, index) => (
              <Text display="inline" key={tag.label} marginRight="4px">
                {tag.label}
                {index < document.documentTags.length - 1 && ','}
              </Text>
            ))}
        </ChakraFlex>
      ),
      name: (
        <ChakraFlex alignItems="center" gap="24px" marginLeft="12px">
          {document?.sensitive ? (
            <ChakraBox>
              <Icon
                aria-label="Flag sensitive"
                color="critical.base"
                name={FAIconName.flagSolid}
              />
            </ChakraBox>
          ) : (
            <ChakraBox width="20px" />
          )}

          <ChakraFlex alignItems="center" gap="10px">
            <ChakraBox id={`${id}__file-icon`}>
              {isInvalidDocument && <BadFileAttachmentView />}

              {shouldShowImage && (
                <ChakraImage
                  alt={document.fileName}
                  boxSize="40px"
                  fit="contain"
                  loading="lazy"
                  src={document?.thumbnailUrl || document?.downloadUrl}
                  title={document.fileName}
                />
              )}

              {!isInvalidDocument && !shouldShowImage && (
                <FileIconsV2
                  height="40px"
                  icon={getFileIconV2(document.fileName)}
                  width="40px"
                />
              )}
            </ChakraBox>

            <ChakraFlex flexDirection="column" gap="4px">
              <Text
                id={`${id}__file-name`}
                maxWidth="400px"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {document.fileName}
              </Text>

              {isInvalidDocument && (
                <Text color="neutral.secondary" id={`${id}__validation-failed`}>
                  {MESSAGE_INVALID_FILE}
                </Text>
              )}
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      ),
      source: (
        <ChakraFlex
          alignItems="center"
          height="60px"
          paddingLeft="12px"
          width="100%"
        >
          <ChakraFlex
            alignItems="center"
            aria-label="View note"
            gap="8px"
            onClick={(e) => {
              if (source.toLowerCase() === DOC_FILE_SOURCE.NOTE.toLowerCase()) {
                handleFileSourceClick(e);
              }
            }}
            onKeyDown={(e) => onKeyDown(e)}
            role="button"
            tabIndex={0}
          >
            <Text color="primary.100">{source}</Text>
            {source.toLowerCase() === DOC_FILE_SOURCE.NOTE.toLowerCase() && (
              <Icon
                color="primary.100"
                name={FAIconName.externalLinkRegular}
                size="sm"
              />
            )}
          </ChakraFlex>

          <ChakraBox marginLeft="auto" paddingRight="12px">
            <FireflyKebabMenu
              aria-label="Document options"
              onMenuOpen={() => {
                trackEvent(DocumentCenterEvents.fileKebabMenuOpened, {
                  Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                  DocumentExternalId: document.documentExternalId,
                  FileName: document.fileName,
                });
              }}
              options={kebabOptions}
            />
          </ChakraBox>
        </ChakraFlex>
      ),

      uploaded: (
        <ChakraFlex alignItems="center" gap="24px" marginLeft="12px">
          {!uploadedBy ? (
            <ChakraToolTip
              bg="white"
              closeOnPointerDown={false}
              color="neutral.secondary"
              label={
                <ChakraBox padding="12px">
                  <Text id={`${id}__uploaded-by-tooltip`} variant="bodyMedium">
                    System
                  </Text>
                </ChakraBox>
              }
              placement="left"
              shouldWrapChildren
            >
              <Avatar
                name={uploadedBy}
                size="xs"
                src={avatarSrc}
                userType="provider"
              />
            </ChakraToolTip>
          ) : (
            <Avatar
              name={uploadedBy}
              size="xs"
              src={avatarSrc}
              tooltipWrapper={({ children }) => (
                <ProviderAvatarTooltipWrapper
                  actionTime={document?.createdAt}
                  isRoleAtAction
                  placement="left"
                  providerExternalId={document.createdBy?.externalId}
                >
                  {children}
                </ProviderAvatarTooltipWrapper>
              )}
              userType="provider"
            />
          )}
          <Text id={`${id}__uploaded-date`}>
            {DateTime.fromISO(document?.createdAt).toFormat('MM/dd/yyyy')}
          </Text>
        </ChakraFlex>
      ),
    },
  };
};

export default DocumentsTableRow;
